<template>
  <FlotoContentLoader :loading="loading">
    <div class="flex flex-col flex-1 overflow-x-hidden">
      <MRow v-if="!disabled">
        <MCol class="flex justify-end">
          <StartStopSoftwareMeterBtn
            v-if="meterData"
            :is-running="!meterData.disabled"
            :handle-action="
              ($event) =>
                handleStartStopMeter({ disabled: !meterData.disabled })
            "
          />
          <ResetSoftwareMeterBtn
            v-if="meterData"
            :handle-reset="handleResetMeter"
          />
          <MButton @click="handleShowDrawer">
            {{ meterData ? $tc('re_configure') : $tc('configure') }}
            {{ $tc('meter') }}
          </MButton>
        </MCol>
      </MRow>
      <MDivider v-if="!disabled" />
      <MRow>
        <MCol v-if="usageInfo.usageDuration" :size="6">
          <BoxNode
            :title="`${$tc('total')} ${$tc('usage_duration')}`"
            :shadow="false"
            :count="usageInfo.usageDuration | duration"
          />
        </MCol>
        <MCol v-if="usageInfo.lastUsedTime" :size="6">
          <BoxNode
            :title="$tc('last_used')"
            :shadow="false"
            :count="`${$options.filters.datetime(
              usageInfo.lastUsedTime
            )} (${$options.filters.timeago(usageInfo.lastUsedTime)})`"
          />
        </MCol>
      </MRow>
      <DiscoveryAgentsList
        v-if="meterData"
        :key="renderCount"
        class="mt-4"
        :hidden-columns="['lastRefreshCallTime']"
        hide-setting-btn
        has-actions
        searchable
        selectable
        :fetch-fn="getSoftwareMeterAgents"
        :bulk-delete-fn="handleBulkDelete"
      >
        <template v-slot:action="{ item, stickAction, unstickAction }">
          <div class="flex">
            <SoftwareMeterUsageDrawer
              :agent-id="item.id"
              :resource-id="resourceId"
              :resource="item"
              class="mx-2"
            />
            <FlotoDeleteBtn
              v-if="!disabled"
              :message="$t('confirm_remove_item', { item: $tc('meter') })"
              @trigger="stickAction"
              @cancel="unstickAction"
              @confirm="handleRemoveAgent(item)"
            >
              <MTooltip>
                <template v-slot:trigger>
                  <MIcon name="trash-alt" />
                </template>
                {{ $t('remove') }}
              </MTooltip>
            </FlotoDeleteBtn>
          </div>
        </template>
      </DiscoveryAgentsList>
      <FlotoDrawer :open="showDrawer" width="65%" @hide="hideDrawer">
        <template v-slot:title>
          {{ meterData ? $tc('re_configure') : $tc('configure') }}
          {{ $tc('meter') }}
        </template>
        <DiscoveryAgentsList
          selectable
          searchable
          hide-setting-btn
          :exclude-items-by-id="excludeItemsById"
          :has-actions="false"
          @selection-change="setSelectedItems"
        />
        <template v-slot:actions="{ hide }">
          <MButton
            :disabled="selectedItems.length === 0"
            outline
            class="mx-1"
            :loading="processing"
            @click="handleConfigure"
          >
            {{ $t('update') }}
          </MButton>
          <MButton variant="default" @click="hide">
            {{ $t('cancel') }}
          </MButton>
        </template>
      </FlotoDrawer>
    </div>
  </FlotoContentLoader>
</template>

<script>
import BoxNode from '@modules/asset-management/components/discovery-component/discovery-service/box-node'
import DiscoveryAgentsList from '@modules/asset-management/components/discovery-component/discovery-agents-list'
import StartStopSoftwareMeterBtn from './start-stop-software-meter-btn'
import ResetSoftwareMeterBtn from './reset-software-meter-btn'
import SoftwareMeterUsageDrawer from './software-meter-usage-drawer'
import {
  getSoftwareMeterAgentsApi,
  getSoftwareMeterApi,
  createSoftwareMeterApi,
  updateSoftwareMeterApi,
  toggleSoftwareMeterApi,
  resetSoftwareMeterApi,
  bulkDeleteSoftwareMeterAgentApi,
} from '../../software-meter-api'
export default {
  name: 'SoftwareMeterTab',
  components: {
    DiscoveryAgentsList,
    StartStopSoftwareMeterBtn,
    ResetSoftwareMeterBtn,
    BoxNode,
    SoftwareMeterUsageDrawer,
  },
  props: {
    resourceId: { type: Number, required: true },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      meterData: null,
      showDrawer: false,
      selectedItems: [],
      processing: false,
      renderCount: 1,
      usageInfo: {},
    }
  },
  computed: {
    excludeItemsById() {
      if (this.meterData) {
        return this.meterData.agentIds || []
      }
      return []
    },
  },
  created() {
    this.getMeterData()
  },
  methods: {
    getMeterData() {
      getSoftwareMeterApi(this.resourceId)
        .then((data) => {
          this.meterData = data
          this.loading = false
          this.$nextTick(() => {
            this.renderCount += 1
          })
        })
        .catch(() => {
          this.meterData = null
          this.showDrawer = false
          this.selectedItems = []
          this.processing = false
          this.loading = false
          this.usageInfo = {}
          this.$nextTick(() => {
            this.renderCount += 1
          })
        })
    },
    getSoftwareMeterAgents(searchCriterias, filters, limit, offset) {
      return getSoftwareMeterAgentsApi(
        searchCriterias,
        this.resourceId,
        limit,
        offset
      ).then((data) => {
        this.usageInfo = {
          usageCount: data.usageCount,
          usageDuration: data.usageDuration,
          lastUsedTime: data.lastUsedTime,
        }
        return {
          items: data.items,
          total: data.total,
        }
      })
    },
    handleResetMeter() {
      return resetSoftwareMeterApi(this.resourceId).then(() => {
        this.getMeterData()
      })
    },
    handleStartStopMeter(data) {
      return toggleSoftwareMeterApi(this.resourceId, data).then((response) => {
        this.meterData = response
      })
    },
    handleBulkDelete(selectedItemIds) {
      return bulkDeleteSoftwareMeterAgentApi(
        this.resourceId,
        selectedItemIds
      ).then(() => {
        this.getMeterData()
      })
    },
    handleShowDrawer() {
      setTimeout(() => {
        this.showDrawer = true
      }, 250)
    },
    hideDrawer() {
      this.showDrawer = false
    },
    setSelectedItems(items) {
      this.selectedItems = items
    },
    handleConfigure() {
      if (this.processing) {
        return
      }
      this.processing = true
      const selectedIds = this.selectedItems.map(({ id }) => id)
      const requestData = {
        agentIds: this.meterData
          ? [...(this.meterData.agentIds || []), ...selectedIds]
          : selectedIds,
      }
      const apiCall = this.meterData
        ? updateSoftwareMeterApi
        : createSoftwareMeterApi
      apiCall(this.resourceId, requestData)
        .then((data) => {
          this.hideDrawer()
          this.meterData = data
          this.$nextTick(() => {
            this.renderCount += 1
          })
        })
        .finally(() => {
          setTimeout(() => {
            this.processing = false
          }, 250)
        })
    },
    handleRemoveAgent(item) {
      return bulkDeleteSoftwareMeterAgentApi(this.resourceId, [item.id]).then(
        () => {
          this.getMeterData()
        }
      )
    },
    // handleUpdate(id) {
    //   const updatedData = {
    //     agentIds: (this.meterData.agentIds || []).filter((a) => a !== id),
    //   }
    //   return updateSoftwareMeterApi(this.resourceId, updatedData).then(
    //     (data) => {
    //       this.meterData = data
    //       this.$nextTick(() => {
    //         this.renderCount += 1
    //       })
    //     }
    //   )
    // },
  },
}
</script>
